// src/contexts/app-boot-context.tsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './auth/auth-context';
import { readStoredTokens } from './auth/helpers';
import { storageSet, storageRemove } from '../platform/storage';
import {
  getCanonicalConversation,
  listConversationsByTheme,
  listMessages,
  listMyThemes,
} from '../api/chat/chat';
import type { MessageResponse } from '../api/chat/chat.types';
import { ENVIRONMENT } from '../env';
import { ensureValidToken } from '../utils/ensure-valid-token';
import { startCtaPreload } from '../utils/preload-cta-videos';

const log = (...args: any[]) => {
  if (ENVIRONMENT === 'DEV') console.log('[AppBoot]', ...args);
};

type AppBootContextValue = {
  authed: boolean;
  /** Auth-level boot: true while session/profile are resolving. */
  bootLoading: boolean;
  ready: boolean;
  /** Preloaded latest message (fetched during boot). */
  latestMessage: MessageResponse | null;
  /** Preloaded conversation ID (fetched during boot). */
  conversationId: string | null;
  /** Preloaded messages (last 10, fetched in parallel with boot). */
  preloadedMessages: MessageResponse[];
  /** Re-fetch latest message on demand. */
  refreshLatestMessage: () => void;
};

const AppBootContext = createContext<AppBootContextValue | null>(null);

const isAuthenticatedSnapshot = (args: { user: any; session: any; sessionLoading: boolean }) => {
  const { user, session, sessionLoading } = args;

  if (sessionLoading) return false;
  if (user) return true;
  if (session?.access_token) return true;

  const stored = readStoredTokens();
  const hasTokens = !!stored.access_token && !!stored.refresh_token;
  if (!hasTokens) return false;

  // If tokens exist but expires_at is missing, trust the tokens —
  // the interceptor will handle refresh on the next 401 if needed.
  const expiresAt = Number(stored.expires_at || 0);
  if (!expiresAt) return true;

  const nowSec = Math.floor(Date.now() / 1000);
  return expiresAt > nowSec;
};

export const AppBootProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, session, sessionLoading, appBootLoading, profileReadyForRouting } = useAuth();

  const authed = useMemo(
    () => isAuthenticatedSnapshot({ user, session, sessionLoading }),
    [user, session, sessionLoading]
  );

  // ── Preload CTA videos as soon as authed ──
  const [ctaVideosReady, setCtaVideosReady] = useState(false);
  useEffect(() => {
    if (!authed) {
      setCtaVideosReady(false);
      return;
    }
    startCtaPreload().then(() => setCtaVideosReady(true));
  }, [authed]);

  /**
   * Auth-level boot:
   * - Wait for session + profile to resolve (enough to make a routing decision).
   * - Wait for CTA videos to preload so the user never sees unloaded placeholders.
   */
  const bootLoading = useMemo(() => {
    if (appBootLoading) return true;
    if (authed && !profileReadyForRouting) return true;
    if (authed && !ctaVideosReady) return true;
    return false;
  }, [appBootLoading, authed, profileReadyForRouting, ctaVideosReady]);

  // ── Preload messages at boot via REST ──
  const [latestMessage, setLatestMessage] = useState<MessageResponse | null>(null);
  const [preloadedMessages, setPreloadedMessages] = useState<MessageResponse[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const didPreloadRef = useRef(false);

  // ── Clear stale preloaded data on logout ──
  useEffect(() => {
    if (!authed) {
      setLatestMessage(null);
      setPreloadedMessages([]);
      setConversationId(null);
      didPreloadRef.current = false;
    }
  }, [authed]);

  const preloadMessages = useCallback(async () => {
    // ── Step 1: ensure token is valid before any API calls ──
    const tokenOk = await ensureValidToken();
    if (!tokenOk) {
      log('preload: no valid token, skipping');
      return;
    }

    // ── Step 2: resolve conversation_id ──
    let convId = localStorage.getItem('conversation_id');

    // If cached, try loading messages immediately
    if (convId) {
      log('preload: trying cached conversation_id =', convId);
      try {
        const msgs = await listMessages(convId, 10);
        if (msgs.length > 0) {
          setConversationId(convId);
          setPreloadedMessages(msgs);
          setLatestMessage(msgs[msgs.length - 1]);
          log('preloaded', msgs.length, 'messages (from cache)');
          return;
        }
        // 0 messages — conversation might be stale, fall through to discovery
        log('preload: cached conversation returned 0 messages, re-discovering…');
      } catch (e) {
        log('preload: cached conversation failed:', e, '— re-discovering…');
      }
      // Clear stale cache
      storageRemove('conversation_id');
      storageRemove('theme_id');
      convId = null;
    }

    // ── Step 3: discover conversation via canonical endpoint ──
    log('preload: resolving canonical conversation…');
    try {
      const canonical = await getCanonicalConversation();
      if (canonical?.id) {
        convId = canonical.id;
        storageSet('theme_id', canonical.theme_id);
        storageSet('conversation_id', convId);
        log('preload: canonical conversation_id =', convId, 'theme =', canonical.theme_id);
      }
    } catch (e) {
      log('preload: canonical endpoint failed, falling back to theme discovery:', e);
      // Fallback: old theme→conversation discovery
      try {
        const themes = await listMyThemes();
        const nonOnboarding = (themes || []).filter((t: any) => t.is_onboarding === false);
        const orderedThemes = [...nonOnboarding, ...(themes || []).filter((t: any) => t.is_onboarding !== false)];

        for (const theme of orderedThemes) {
          if (!theme?.id) continue;
          const convs = await listConversationsByTheme(theme.id);
          if (convs?.[0]?.id) {
            convId = convs[0].id;
            storageSet('theme_id', theme.id);
            storageSet('conversation_id', convId);
            log('preload: fallback discovered conversation_id =', convId, 'theme =', theme.id);
            break;
          }
        }
      } catch (fallbackErr) {
        log('preload: fallback discovery also failed:', fallbackErr);
      }
    }

    if (!convId) {
      log('preload: no conversations found');
      return;
    }

    // ── Step 4: load messages for discovered conversation ──
    log('preload: loading messages for', convId);
    setConversationId(convId);

    try {
      const msgs = await listMessages(convId, 10);
      if (msgs.length > 0) {
        setPreloadedMessages(msgs);
        setLatestMessage(msgs[msgs.length - 1]);
      }
      log('preloaded', msgs.length, 'messages');
    } catch (e) {
      log('preload: listMessages failed:', e);
    }
  }, []);

  // Listen for boot:conversation event (seeded from combined boot endpoint)
  useEffect(() => {
    const handleBootConversation = (e: Event) => {
      const conv = (e as CustomEvent).detail;
      if (!conv?.id) return;
      log('boot:conversation received, conversation_id =', conv.id);

      // Seed localStorage so future preloads use the cached ID
      storageSet('conversation_id', conv.id);
      if (conv.theme_id) storageSet('theme_id', conv.theme_id);

      setConversationId(conv.id);
      didPreloadRef.current = true;

      // Load messages in background
      ensureValidToken().then((tokenOk) => {
        if (!tokenOk) return;
        listMessages(conv.id, 10).then((msgs) => {
          if (msgs.length > 0) {
            setPreloadedMessages(msgs);
            setLatestMessage(msgs[msgs.length - 1]);
          }
          log('preloaded', msgs.length, 'messages (from boot event)');
        }).catch((err) => {
          log('boot:conversation message preload failed:', err);
        });
      });
    };
    window.addEventListener('boot:conversation', handleBootConversation);
    return () => window.removeEventListener('boot:conversation', handleBootConversation);
  }, []);

  // Start fetching as soon as authed — don't wait for bootLoading to finish
  // Skips if boot:conversation already seeded the data
  useEffect(() => {
    if (!authed || didPreloadRef.current) return;
    didPreloadRef.current = true;
    preloadMessages();
  }, [authed, preloadMessages]);

  const refreshLatestMessage = useCallback(() => {
    didPreloadRef.current = false;
    preloadMessages();
  }, [preloadMessages]);

  const value = useMemo<AppBootContextValue>(
    () => ({
      authed,
      bootLoading,
      ready: !bootLoading,
      latestMessage,
      conversationId,
      preloadedMessages,
      refreshLatestMessage,
    }),
    [authed, bootLoading, latestMessage, conversationId, preloadedMessages, refreshLatestMessage]
  );

  return <AppBootContext.Provider value={value}>{children}</AppBootContext.Provider>;
};

export const useAppBoot = () => {
  const ctx = useContext(AppBootContext);
  if (!ctx) throw new Error('useAppBoot must be used within AppBootProvider');
  return ctx;
};
